<main class="login-shell">
  <section class="login-hero">
    <p class="eyebrow">GO Evolution</p>
    <h1>Secure sign-in for the GO Evolution</h1>
    <p class="summary">
      The application loads its OpenID Connect configuration from GOEvProxy and starts the Authorization Code Flow when the user chooses to sign in.
    </p>

    @if (isDevEnvironment()) {
      <dl class="facts">
        <div>
          <dt>Flow</dt>
          <dd>OIDC Authorization Code</dd>
        </div>
        <div>
          <dt>Settings source</dt>
          <dd>GOEvProxy /apiproxy/v1/settings/settings</dd>
        </div>
        <div>
          <dt>Token storage</dt>
          <dd>Browser session storage</dd>
        </div>
      </dl>
    }
  </section>

  <section class="login-panel">
    <div class="panel-card">
      <p class="panel-label">Account access</p>

      @if (auth.status() === 'loading' || auth.status() === 'idle') {
        <div class="state-block">
          <span class="spinner" aria-hidden="true"></span>
          <div>
            <h2>Loading sign-in settings</h2>
            <p>Reading the OIDC configuration from the proxy and preparing the browser client.</p>
          </div>
        </div>
      } @else if (auth.status() === 'error') {
        <div class="state-block error">
          <h2>Authentication setup failed</h2>
          <p>{{ auth.errorMessage() }}</p>
        </div>
      } @else if (auth.isAuthenticated()) {
        <div class="state-block success">
          <h2>Signed in</h2>
          <p>You already have a valid session for this browser tab.</p>

          @if (auth.displayName(); as displayName) {
            <p class="identity">{{ displayName }}</p>
          }

          <div class="action-group">
            @if (isDevEnvironment()) {
              <button type="button" class="secondary-button" (click)="getRoles()">
                @if (auth.rolesLoading()) {
                  Loading roles...
                } @else {
                  Get roles
                }
              </button>
            }

            @if (auth.accessToken() && isDevEnvironment()) {
              <button type="button" class="secondary-button" (click)="copyAccessToken()">Copy Access Token</button>
            }

            <button type="button" class="secondary-button hangfire-link" (click)="openHangfireDashboard()">Open Sync Dashboard</button>

            <button type="button" class="primary-button" (click)="signOut()">Sign out</button>
          </div>

          @if (auth.rolesError(); as rolesError) {
            <p class="roles-feedback error-text">{{ rolesError }}</p>
          }

          @if (auth.rolesUser() || auth.roles().length) {
            <div class="roles-panel">
              <span>Roles</span>

              @if (auth.rolesUser(); as rolesUser) {
                <strong>{{ rolesUser }}</strong>
              }

              @if (auth.roles().length) {
                <ul class="roles-list">
                  @for (role of auth.roles(); track role) {
                    <li>{{ role }}</li>
                  }
                </ul>
              } @else {
                <p class="roles-feedback">No roles were returned for this user.</p>
              }
            </div>
          }

          @if (copyFeedback(); as copyFeedback) {
            <p class="copy-feedback">{{ copyFeedback }}</p>
          }
        </div>
      } @else {
        <div class="state-block">
          <h2>Sign in</h2>
          <p>Continue with your organization account to start the OpenID Connect login flow.</p>

          <button type="button" class="primary-button" (click)="signIn()">Sign in with OpenID Connect</button>
        </div>
      }

      @if (auth.settings(); as settings) {
        @if (isDevEnvironment()) {
          <div class="details">
            <div>
              <span>Issuer</span>
              <strong>{{ settings.issuer }}</strong>
            </div>
            <div>
              <span>Scope</span>
              <strong>{{ settings.scope }}</strong>
            </div>
            <div>
              <span>Redirect</span>
              <strong>{{ settings.redirectUri }}</strong>
            </div>
          </div>
        }
      }
    </div>
  </section>

  @if (proxyVersion(); as proxyVersion) {
    <p class="login-version">{{ proxyVersion }}</p>
  }
</main>